import { QueryObserver } from "@tanstack/query-core";
import { getWorkspaceInvites } from "../../../../../backendapi/workspaceApi";
import { queryClient } from "../../../../queryClient";
import { ActivityUIKind, activityQueryKeys } from "../constants";
import type { ActivitySource, ActivityViewData, InviteMeta } from "../types";
import { toDate } from "./utils";
import type { FirestoreTimestampLike } from "./types";

interface InviteDoc {
    id: string;
    workspaceId: string;
    workspaceName?: string;
    workspaceUrl?: string;
    fromEmail?: string;
    token: string;
    sent?: FirestoreTimestampLike;
}

/**
 * Follows the backend's `activityService.buildEventId` convention
 * (`{workspaceId}:{kind}:{entityId}`) so invite IDs are consistent with
 * real `activity_events` IDs and work with the shared read-state store
 * (`activity_event_interactions`).
 */
function buildInviteId(workspaceId: string, inviteDocId: string): string {
    return `${workspaceId}:invite:${inviteDocId}`;
}

/** QueryFn — fetches all workspace invites for the current user. */
async function fetchInvites(): Promise<ActivityViewData[]> {
    const invites: InviteDoc[] = await getWorkspaceInvites();
    const activities: ActivityViewData[] = [];

    for (const invite of invites) {
        if (!invite) continue;
        const meta: InviteMeta = {
            workspaceId: invite.workspaceId,
            token: invite.token,
            workspaceUrl: invite.workspaceUrl
        };
        activities.push({
            id: buildInviteId(invite.workspaceId, invite.id),
            kind: ActivityUIKind.Invite,
            title: invite.workspaceName || "Workspace invite",
            description: invite.fromEmail ? `Invited by ${invite.fromEmail}` : null,
            icon: "far fa-building",
            createdAt: toDate(invite.sent),
            meta
        });
    }

    return activities;
}

/**
 * ActivitySource for workspace invites (user-scoped, cross-workspace).
 *
 * Backed by a QueryObserver for polling, caching, error retry, and
 * cache invalidation. Renderers trigger refetch after accept/decline via
 * `queryClient.invalidateQueries({ queryKey: activityQueryKeys.all })`.
 */
class InviteSource implements ActivitySource {
    private userId: string;

    constructor(userId: string) {
        this.userId = userId;
    }

    subscribe(callback: (activities: ActivityViewData[]) => void): () => void {
        const observer = new QueryObserver(queryClient, {
            queryKey: activityQueryKeys.invites(this.userId),
            queryFn: fetchInvites,
            refetchInterval: 60_000
        });

        const unsubscribe = observer.subscribe((result) => {
            if (result.data) {
                callback(result.data);
            } else if (result.error) {
                callback([]);
            }
        });

        return () => {
            unsubscribe();
            observer.destroy();
        };
    }
}

export { InviteSource };
